import fastSort from 'fast-sort';
import eventsApi from '@/api/events';
import agendaApi from '@/api/agenda';
import votingsApi from '@/api/votings';
import participantApi from '@/api/participants';
import teamApi from '@/api/teams';
import {
	checkRoleOwned,
	duplicateVar,
	isParticipantHaveVoteRight,
	isParticipantNewLeave,
	isParticipantActive,
	isParticipantCheckIn,
	isEventCheckInRequired,
	isEventCheckFloorEnabledAndParticipantAtTheFloor,
} from '@/lib/helper';

const RESET_STATES = 'RESET_STATES';
const RESET_AGENDAS = 'RESET_AGENDAS';
const SET_IS_FETCHING = 'SET_IS_FETCHING';
const SET_IS_FETCHING_AGENDAS = 'SET_IS_FETCHING_AGENDAS';
const SET_IS_FETCHING_PARTICIPANTS = 'SET_IS_FETCHING_PARTICIPANTS';
const SET_IS_FETCHING_TEAMS = 'SET_IS_FETCHING_TEAMS';
const SET_EVENT = 'SET_EVENT';
const SET_AGENDAS = 'SET_AGENDAS';
const SET_AGENDA = 'SET_AGENDA';
const SET_SHOW_ADD_SUGGESTION = 'SET_SHOW_ADD_SUGGESTION';
const SET_SHOW_ADD_RECOMMENDATION = 'SET_SHOW_ADD_RECOMMENDATION';
const SET_SHOW_ADD_SPEECH = 'SET_SHOW_ADD_SPEECH';
const SET_CURRENT_ROLE = 'SET_CURRENT_ROLE';
const SET_AGENDA_BOOKMARK = 'SET_AGENDA_BOOKMARK';
const SET_PARTICIPANTS = 'SET_PARTICIPANTS';
const ADD_PARTICIPANTS = 'ADD_PARTICIPANTS';
const SET_PARTICIPANT_PAGE = 'SET_PARTICIPANT_PAGE';
const SET_TEAMS = 'SET_TEAMS';
const SET_STARTING_ROLE = 'SET_STARTING_ROLE';
const SET_IS_FETCHING_PARTICIPANT = 'SET_IS_FETCHING_PARTICIPANT';
const SET_PARTICIPANT = 'SET_PARTICIPANT';

const SET_SHOW_MODAL_MATERIAL_CHOOSER = 'SET_SHOW_MODAL_MATERIAL_CHOOSER';
const SET_SELECTED_MATERIAL = 'SET_SELECTED_MATERIAL';
const SET_SELECTED_VOTING = 'SET_SELECTED_VOTING';
const SET_VOTING = 'SET_VOTING';
const SET_SHOW_MODAL_VOTING = 'SET_SHOW_MODAL_VOTING';
const SET_SHOW_FORM_ADD_SUGGESTION = 'SET_SHOW_FORM_ADD_SUGGESTION';
const SET_SHOW_VIEW_MATERIALS = 'SET_SHOW_VIEW_MATERIALS';
const SET_SELECTED_AGENDA_ASSET = 'SET_SELECTED_AGENDA_ASSET';
const SET_SELECTED_SUGGESTIONS = 'SET_SELECTED_SUGGESTIONS';
const SET_SELECTED_SUGGESTION = 'SET_SELECTED_SUGGESTION';
const SET_IS_SHOW_MODAL_SUGGESTIONS = 'SET_IS_SHOW_MODAL_SUGGESTIONS';
const SET_FILTERED_SUGGESTIONS = 'SET_FILTERED_SUGGESTIONS';
const SET_SUGGESTION_CATEGORY = 'SET_SUGGESTION_CATEGORY';
const SET_IS_REPLY_SUGGESTION = 'SET_IS_REPLY_SUGGESTION';
const SET_SUGGESTION_REPLY = 'SET_SUGGESTION_REPLY';
const SET_SEARCH_PDF_KEYWORD = 'SET_SEARCH_PDF_KEYWORD';
const SET_SUGGESTIONS = 'SET_SUGGESTIONS';
const SET_LAST_PAGE_SUGGESTIONS = 'SET_LAST_PAGE_SUGGESTIONS';
const SET_CURRENT_PAGE_SUGGESTIONS = 'SET_CURRENT_PAGE_SUGGESTIONS';
const SET_IS_SHOW_MODAL_VOTING_SHARES = 'SET_IS_SHOW_MODAL_VOTING_SHARES';
const SET_SUGGESTIONS_SORT_BY = 'SET_SUGGESTIONS_SORT_BY';
const SET_VOTINGS = 'SET_VOTINGS';
const SET_IS_SHOW_AGENDA_MATERIAL = 'SET_IS_SHOW_AGENDA_MATERIAL';

const state = {
	event: null,
	isFetchingAgenda: false,
	agendas: [],
	isFetchingParticipants: false,
	participants: [],
	participantPaginated: true,
	participantLimit: 100,
	participantPage: 1,
	participantTotalPage: 1,
	isFetchingParticipant: false,
	participant: null,
	isFetchingTeam: false,
	teams: [],
	agenda: null,
	agendaTime: 0,
	isConnected: false,
	addSpeechType: 'normal',
	speechProgress: 0,
	isFetching: false,
	currentRole: null,
	suggestionsShared: [],

	// add suggestion or recommendation
	isShowAddSuggestion: false,
	isAddSuggestion: false,
	isAddRecommendation: false,

	// speech
	isShowAddSpeech: false,

	// share/compare/voting suggestions
	isShowModalSuggestions: false,
	isShareSuggestions: false,
	isCompareSuggestions: false,
	isVotingSuggestions: false,

	// agenda material
	isShowModalMaterialChooser: false,
	selectedMaterial: null,
	selectedMaterialNumberFrom: 0,
	selectedMaterialNumberTo: 0,

	// voting
	selectedVoting: null,
	voting: null,
	votingTime: 0,
	isShowModalVoting: false,

	isShowFormAddSuggestion: false,
	isShowViewMaterials: false,
	selectedAgendaAsset: null,
	selectedSuggestions: [],
	selectedSuggestion: null,
	filteredSuggestions: [],
	suggestions: [],
	suggestionCategory: 'suggestion',
	isReplySuggestion: false,
	suggestionReply: null,
	searchPdfKeyword: '',
	lastPageSuggestions: 1,
	currentPageSuggestions: 1,
	suggestionsSortBy: 'asc',

	// Voting Shares
	isShowModalVotingShares: false,
	votingShares: [],

	// Votings
	votings: [],

	// Agenda material
	isShowAgendaMaterial: false,
	selectedAgendaMaterial: null,
};

const getters = {
	isFetching: state => state.isFetching,
	event: state => state.event,
	isFetchingAgenda: state => state.isFetchingAgenda,
	agendas: state => state.agendas,
	isFetchingParticipants: state => state.isFetchingParticipants,
	participants: state => state.participants,
	participantPaginated: state => state.participantPaginated,
	participantLimit: state => state.participantLimit,
	participantPage: state => state.participantPage,
	participantTotalPage: state => state.participantTotalPage,
	isFetchingParticipant: state => state.isFetchingParticipant,
	participant: state => state.participant,
	isEventCheckFloorEnabledAndParticipantAtTheFloor: (state) => {
		const isValid = isEventCheckFloorEnabledAndParticipantAtTheFloor(state.event, state.participant);
		return isValid;
	},
	isFetchingTeam: state => state.isFetchingTeam,
	isEventActive: (state, getters) => {
		let isActive = false;
		if (getters.event) isActive = getters.event.status === 'active';
		return isActive;
	},
	teams: state => state.teams,
	parentagendas: (state) => {
		const parents = [];
		for (let i = 0; i < state.agendas.length; i++) {
			if (!state.agendas[i].parent_id) parents.push(state.agendas[i]);
		}
		return parents;
	},
	subAgendas: (state) => {
		const subAgendas = [];
		for (let i = 0; i < state.agendas.length; i++) {
			if (state.agendas[i].parent_id) subAgendas.push(state.agendas[i]);
		}
		return subAgendas;
	},
	agenda: state => state.agenda,
	agendaTime: state => state.agendaTime,
	currentRole: state => state.currentRole,
	activeAgenda: state => state.agendas.find(curr => curr.status === 'active'),
	activeAgendaSpeeches: (state, getters) => (getters.activeAgenda ? getters.activeAgenda.speeches : []),
	activeAgendaSpeechesSorted: (state, getters) => {
		const queuedSpeakers = getters.activeAgendaSpeeches.filter(speaker => speaker.status === 'waiting');
		let sortedSpeakers = queuedSpeakers;

		// Filter sort
		let filterSort = [
			{ asc: u => u.order },
		];

		// Wrong
		let wrongSpeakers = queuedSpeakers.filter(speaker => speaker.type === 'wrong');
		wrongSpeakers = fastSort(wrongSpeakers).by(filterSort);

		// Intro
		let introSpeakers = queuedSpeakers.filter(speaker => speaker.type === 'intro');
		introSpeakers = fastSort(introSpeakers).by(filterSort);

		// Reply
		let replySpeakers = queuedSpeakers.filter(speaker => speaker.type === 'reply');
		replySpeakers = fastSort(replySpeakers).by(filterSort);

		// Summary
		let summarySpeakers = queuedSpeakers.filter(speaker => speaker.type === 'summary');
		summarySpeakers = fastSort(summarySpeakers).by(filterSort);

		// Speech times grouped
		if (getters.agenda && getters.speechSettings.is_speech_times_grouped) {
			filterSort = [
				{ asc: u => u.speech_times },
				{ asc: u => u.order },
			];
		}

		// Normal
		let normalSpeakers = queuedSpeakers.filter(speaker => speaker.type === 'normal');
		normalSpeakers = fastSort(normalSpeakers).by(filterSort);

		// Combine
		let combineSpeakers = [];
		combineSpeakers = combineSpeakers.concat(wrongSpeakers, introSpeakers, replySpeakers, normalSpeakers, summarySpeakers);
		sortedSpeakers = combineSpeakers;

		return sortedSpeakers;
	},
	suggestions: state => state.suggestions,
	speeches: state => (state.agenda ? state.agenda.speeches : []),
	speechesSorted: (state, getters) => {
		const queuedSpeakers = getters.speeches.filter(speaker => speaker.status === 'waiting');
		let sortedSpeakers = queuedSpeakers;

		// Filter sort
		let filterSort = [
			{ asc: u => u.order },
		];

		// Wrong
		let wrongSpeakers = queuedSpeakers.filter(speaker => speaker.type === 'wrong');
		wrongSpeakers = fastSort(wrongSpeakers).by(filterSort);

		// Intro
		let introSpeakers = queuedSpeakers.filter(speaker => speaker.type === 'intro');
		introSpeakers = fastSort(introSpeakers).by(filterSort);

		// Reply
		let replySpeakers = queuedSpeakers.filter(speaker => speaker.type === 'reply');
		replySpeakers = fastSort(replySpeakers).by(filterSort);

		// Summary
		let summarySpeakers = queuedSpeakers.filter(speaker => speaker.type === 'summary');
		summarySpeakers = fastSort(summarySpeakers).by(filterSort);

		// Speech times grouped
		if (getters.agenda && getters.speechSettings.is_speech_times_grouped) {
			filterSort = [
				{ asc: u => u.speech_times },
				{ asc: u => u.order },
			];
		}

		// Normal
		let normalSpeakers = queuedSpeakers.filter(speaker => speaker.type === 'normal');
		normalSpeakers = fastSort(normalSpeakers).by(filterSort);

		// Combine
		let combineSpeakers = [];
		combineSpeakers = combineSpeakers.concat(wrongSpeakers, introSpeakers, replySpeakers, normalSpeakers, summarySpeakers);
		sortedSpeakers = combineSpeakers;

		return sortedSpeakers;
	},
	activeSpeaker: (state, getters) => {
		if (getters.activeAgenda) return getters.activeAgenda.speeches.find(speech => speech.status === 'active');
		return null;
	},
	nextSpeaker: (state, getters) => {
		const speaker = getters.activeSpeaker && getters.activeAgendaSpeechesSorted.length > 0 ? getters.activeAgendaSpeechesSorted[0] : null;
		return speaker;
	},
	getSpeechById: state => (id) => {
		const theSpeech = state.agendas.find(agenda => agenda.speeches.find(speech => speech.id === id));
		return theSpeech;
	},
	agendaMaterials: state => (state.agenda ? state.agenda.materials : []),
	documents: (state, getters) => getters.agendaMaterials.filter(curr => curr.type === 'material_text' || curr.type === 'material_file'),
	isAdmin: (state, getters) => getters.currentRole === 'admin' || getters.currentRole === 'moderator',
	isAdminEvent: (state, getters) => getters.currentRole === 'admin',
	isModerator: (state, getters) => getters.currentRole === 'moderator',
	isSecretary: (state, getters) => getters.currentRole === 'secretary',
	isEditorial: (state, getters) => getters.currentRole === 'editorial',
	isDelegate: (state, getters) => getters.currentRole !== 'admin' && getters.currentRole !== 'moderator',
	isDelegateRole: (state, getters) => getters.currentRole === 'delegate',
	isGuest: (state, getters) => getters.currentRole === 'guest',
	isSetAgendaRole: (state, getters) => getters.currentRole === 'set_agenda',
	isHaveSecretaryRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'secretary');
		return isHave;
	},
	isHaveEditorialRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'editorial');
		return isHave;
	},
	isHaveAdminRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'admin');
		return isHave;
	},
	isHaveModeratorRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'moderator');
		return isHave;
	},
	isHaveRegistrationRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'registration');
		return isHave;
	},
	isHaveTechnicRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'technic');
		return isHave;
	},
	isHaveLeaderRole: (state, getters) => {
		const isHave = checkRoleOwned(getters.participant, 'leader');
		return isHave;
	},
	isShowStartSpeaker: (state, getters) => !getters.activeSpeaker && getters.speechesSorted.length > 0,
	isShowStopSpeaker: (state, getters) => !!getters.activeSpeaker,
	isShowAddSuggestion: state => state.isShowAddSuggestion,
	isAddSuggestion: state => state.isAddSuggestion,
	isAddRecommendation: state => state.isAddRecommendation,
	isShowAddSpeech: state => state.isShowAddSpeech,
	addSpeechType: state => state.addSpeechType,
	speechProgress: state => state.speechProgress,
	suggestionSettings: (state, getters) => (getters.agenda ? JSON.parse(getters.agenda.suggestion_settings) : false),
	speechSettings: (state, getters) => (getters.agenda ? JSON.parse(getters.agenda.speech_settings) : false),
	materialSettings: (state, getters) => (getters.agenda ? JSON.parse(getters.agenda.material_settings) : false),
	eventSuggestionSettings: (state, getters) => (getters.event ? JSON.parse(getters.event.suggestion_settings) : false),
	eventSpeechSettings: (state, getters) => (getters.event ? JSON.parse(getters.event.speech_settings) : false),
	suggestionsShared: state => state.suggestionsShared,
	isShareSuggestions: state => state.isShareSuggestions,
	isCompareSuggestions: state => state.isCompareSuggestions,
	isVotingSuggestions: state => state.isVotingSuggestions,
	isShowModalSuggestions: state => state.isShowModalSuggestions,
	isShowModalMaterialChooser: state => state.isShowModalMaterialChooser,
	selectedMaterial: state => state.selectedMaterial,
	selectedMaterialNumberFrom: state => state.selectedMaterialNumberFrom,
	selectedMaterialNumberTo: state => state.selectedMaterialNumberTo,
	selectedVoting: state => state.selectedVoting,
	// votings: state => (state.agenda ? state.votings : []),
	votings: state => state.votings,
	voting: state => state.voting,
	votingTime: state => state.votingTime,
	isShowModalVoting: state => state.isShowModalVoting,
	participantsCanVote: state => (state.event && state.participants ? state.participants.filter(curr => curr.capabilities.indexOf('vote') !== -1) : []),
	participantsHasLoggedIn: state => (state.event && state.participants ? state.participants.filter(curr => curr.user.logged_in_at) : []),
	participantsAway: state => (state.event && state.participants ? state.participants.filter(curr => !curr.is_active || curr.leave_time) : []),
	isShowFormAddSuggestion: state => state.isShowFormAddSuggestion,
	isShowViewMaterials: state => state.isShowViewMaterials,
	selectedAgendaAsset: state => state.selectedAgendaAsset,
	selectedSuggestions: state => state.selectedSuggestions,
	selectedSuggestion: state => state.selectedSuggestion,
	filteredSuggestions: state => state.filteredSuggestions,
	suggestionCategory: state => state.suggestionCategory,
	isReplySuggestion: state => state.isReplySuggestion,
	suggestionReply: state => state.suggestionReply,
	searchPdfKeyword: state => state.searchPdfKeyword,
	lastPageSuggestions: state => state.lastPageSuggestions,
	currentPageSuggestions: state => state.currentPageSuggestions,
	isShowModalVotingShares: state => state.isShowModalVotingShares,
	votingShares: state => state.votingShares,
	suggestionsSortBy: state => state.suggestionsSortBy,
	isShowAgendaMaterial: state => state.isShowAgendaMaterial,
	selectedAgendaMaterial: state => state.selectedAgendaMaterial,
};

const mutations = {
	[RESET_STATES](state) {
		state.event = null;
		state.agendas = [];
		state.participants = [];
		state.participant = null;
		state.agenda = null;
		state.agendaTime = 0;
		state.isConnected = false;
		state.addSpeechType = 'normal';
		state.speechProgress = 0;
		state.isFetching = false;
		state.currentRole = null;
		state.suggestionsShared = [];

		// add suggestion or recommendation
		state.isShowAddSuggestion = false;
		state.isAddSuggestion = false;
		state.isAddRecommendation = false;

		// speech
		state.isShowAddSpeech = false;

		// share/compare/voting suggestions
		state.isShowModalSuggestions = false;
		state.isShareSuggestions = false;
		state.isCompareSuggestions = false;
		state.isVotingSuggestions = false;

		// agenda material
		state.isShowModalMaterialChooser = false;
		state.selectedMaterial = null;
		state.selectedMaterialNumberFrom = 0;
		state.selectedMaterialNumberTo = 0;

		// voting
		state.selectedVoting = null;
		state.voting = null;
		state.votingTime = 0;

		state.isShowModalVoting = false;

		state.isShowFormAddSuggestion = false;
		state.isShowViewMaterials = false;

		state.selectedAgendaAsset = null;

		state.selectedSuggestions = [];

		state.filteredSuggestions = [];

		state.suggestionCategory = 'suggestion';

		state.isReplySuggestion = false;
		state.suggestionReply = null;
		state.searchPdfKeyword = '';
		state.lastPageSuggestions = 1;
		state.currentPageSuggestions = 1;

		state.isShowModalVotingShares = false;
		state.votingShares = [];

		state.suggestionsSortBy = 'asc';

		state.votings = [];
	},
	[RESET_AGENDAS](state) {
		state.agendas = [];
		state.agendaTime = 0;
		state.speechProgress = 0;
	},
	[SET_CURRENT_ROLE](state, role) {
		state.currentRole = role;
	},
	[SET_IS_FETCHING](state, isFetching) {
		state.isFetching = isFetching;

		// reset modal suggestion
		state.isShareSuggestions = false;
		state.isCompareSuggestions = false;
		state.isShowModalSuggestions = false;
		state.isVotingSuggestions = false;
		state.suggestionsShared = [];
	},
	[SET_IS_FETCHING_PARTICIPANTS](state, isFetchingParticipants) {
		state.isFetchingParticipants = isFetchingParticipants;
	},
	[SET_PARTICIPANTS](state, participants) {
		state.participants = participants;
	},
	[ADD_PARTICIPANTS](state, items) {
		const newItems = state.participants.concat(items);
		state.participants = newItems;
	},
	[SET_PARTICIPANT_PAGE](state, page) {
		state.participantPage = page;
	},
	[SET_IS_FETCHING_TEAMS](state, isFetching) {
		state.isFetchingTeam = isFetching;
	},
	[SET_IS_FETCHING_AGENDAS](state, isFetching) {
		state.isFetchingAgenda = isFetching;
	},
	[SET_FILTERED_SUGGESTIONS](state, suggestions) {
		state.filteredSuggestions = suggestions;
	},
	[SET_EVENT](state, event) {
		state.event = event;
		state.agenda = null;
	},
	[SET_AGENDAS](state, agendas) {
		// Reset
		state.agendas = [];

		state.agendas = agendas;
	},
	[SET_TEAMS](state, teams) {
		state.teams = teams;
	},
	[SET_STARTING_ROLE](state, participant) {
		const roles = JSON.parse(participant.roles);
		const isAdmin = roles.indexOf('admin');
		const isModerator = roles.indexOf('moderator');
		const isSecretary = roles.indexOf('secretary');
		const isEditorial = roles.indexOf('editorial');
		const isDelegate = roles.indexOf('delegate');

		if (isAdmin !== -1) {
			state.currentRole = 'admin';
		} else if (isModerator !== -1) {
			state.currentRole = 'moderator';
		} else if (isSecretary !== -1) {
			state.currentRole = 'secretary';
		} else if (isEditorial !== -1) {
			state.currentRole = 'editorial';
		} else if (isDelegate !== -1) {
			state.currentRole = 'delegate';
		} else {
			state.currentRole = roles[0];
		}
	},
	[SET_AGENDA](state, { agenda }) {
		if (agenda) {
			const selectedAgenda = duplicateVar(agenda);
			if (typeof agenda.settings === 'string') selectedAgenda.settings = JSON.parse(selectedAgenda.settings);
			if (typeof selectedAgenda.assets === 'string') selectedAgenda.assets = JSON.parse(selectedAgenda.assets);
			state.agenda = duplicateVar(selectedAgenda);
			state.selectedSuggestions = [];
			state.selectedSuggestion = null;
		} else {
			state.agenda = null;
			state.votings = [];
		}

		// Reset view to agenda
		state.isShowFormAddSuggestion = false;
		state.isShowViewMaterials = false;
		state.selectedVoting = null;
		state.selectedAgendaAsset = null;
		state.selectedSuggestions = [];
		state.selectedSuggestion = null;
	},
	[SET_SHOW_ADD_SUGGESTION](state, value) {
		state.isShowAddSuggestion = value;
		state.isAddSuggestion = value;
		state.isAddRecommendation = false;

		state.selectedMaterial = null;
		state.selectedMaterialNumberFrom = 0;
		state.selectedMaterialNumberTo = 0;
	},
	[SET_SHOW_ADD_RECOMMENDATION](state, value) {
		state.isShowAddSuggestion = value;
		state.isAddRecommendation = value;
		state.isAddSuggestion = false;

		state.selectedMaterial = null;
		state.selectedMaterialNumberFrom = 0;
		state.selectedMaterialNumberTo = 0;
	},
	// eslint-disable-next-line
	[SET_SHOW_ADD_SPEECH](state, { value, type = 'normal', isReplySuggestion = false, suggestionReply = null }) {
		state.isShowAddSpeech = value;
		state.addSpeechType = type;
		state.isReplySuggestion = isReplySuggestion;
		state.suggestionReply = suggestionReply;
	},
	[SET_AGENDA_BOOKMARK](state, { agendaId, agendaBookmark }) {
		for (let i = 0; i < state.agendas.length; i++) {
			if (state.agendas[i].id === agendaId) {
				state.agendas[i].agenda_bookmark = agendaBookmark;
				break;
			}
		}
	},
	[SET_SHOW_MODAL_MATERIAL_CHOOSER](state, value) {
		state.isShowModalMaterialChooser = value;
	},
	[SET_SELECTED_MATERIAL](state, { material, from, to }) {
		state.selectedMaterial = material;
		state.selectedMaterialNumberFrom = from;
		state.selectedMaterialNumberTo = to;
	},
	[SET_VOTING](state, payload) {
		const { data, getters } = payload;

		// If no participant
		if (!state.participant) return;

		// Check voter
		if (state) {
			const participant = state.participant;
			const event = state.event;
			const isLeave = isParticipantNewLeave(participant);
			const isHaveVoteRight = isParticipantHaveVoteRight(participant);
			const isActive = isParticipantActive(participant);
			const isParticipantAtTheFloor = isEventCheckFloorEnabledAndParticipantAtTheFloor(event, participant);
			const isAllowed = !isLeave && isActive && isHaveVoteRight && isParticipantAtTheFloor;
			let isVoter = isAllowed;
			if (event) {
				const isCheckInRequired = isEventCheckInRequired(event);
				if (isCheckInRequired) {
					const isCheckIn = isParticipantCheckIn(participant);
					isVoter = isAllowed && isCheckIn;
				}
			}

			// If not voter
			if (!isVoter) return;
		}

		if (data) {
			state.voting = data;

			// check is participant has been vote or not
			let hasVoted = false;
			for (let i = 0; i < data.voting_options.length; i++) {
				const find = data.voting_options[i].voting_results.find(result => result.participant_id === getters.participant.id);
				if (find) {
					hasVoted = true;
					break;
				}
			}
			if (hasVoted) return;
			state.isShareSuggestions = false;
			state.isCompareSuggestions = false;
			state.isVotingSuggestions = true;
			if (data.type === 'suggestion') {
				const suggestions = data.voting_options.map(curr => curr.suggestion);
				if (state.voting) {
					let settings = duplicateVar(state.voting.settings);
					if (typeof settings === 'string') settings = JSON.parse(settings);
					const isSingleSuggestion = settings.is_single_suggestion;
					if (isSingleSuggestion) {
						// remove duplicate object
						const filtered = suggestions.reduce((unique, o) => {
							if (!unique.some(obj => obj.id === o.id)) unique.push(o);
							return unique;
						}, []);
						state.suggestionsShared = filtered;
					} else {
						state.suggestionsShared = suggestions;
					}
				}
			} else {
				state.suggestionsShared = [];
			}

			if (data.type === 'suggestion') {
				state.isShowModalSuggestions = true;
			} else {
				state.isShowModalVoting = true;
			}
		}
	},
	[SET_SELECTED_VOTING](state, selectedVoting) {
		state.selectedVoting = duplicateVar(selectedVoting);
		// state.selectedVoting = Object.assign({}, state.selectedVoting, selectedVoting);
	},
	[SET_SHOW_MODAL_VOTING](state, value) {
		state.isShowModalVoting = value;
	},
	[SET_SHOW_FORM_ADD_SUGGESTION](state, value) {
		state.isShowFormAddSuggestion = value;
	},
	[SET_SHOW_VIEW_MATERIALS](state, value) {
		state.isShowViewMaterials = value;
	},
	[SET_SELECTED_AGENDA_ASSET](state, value) {
		state.selectedAgendaAsset = value;
		if (value !== 'voting') state.selectedVoting = null;
	},
	[SET_SELECTED_SUGGESTIONS](state, suggestions) {
		state.selectedSuggestions = suggestions;
	},
	[SET_SUGGESTIONS](state, suggestions) {
		state.suggestions = suggestions;
	},
	[SET_SELECTED_SUGGESTION](state, suggestion) {
		state.selectedSuggestion = suggestion;
	},
	[SET_IS_SHOW_MODAL_SUGGESTIONS](state, isShow) {
		state.isShowModalSuggestions = isShow;
	},
	[SET_SUGGESTION_CATEGORY](state, category) {
		state.suggestionCategory = category;
	},
	[SET_IS_REPLY_SUGGESTION](state, isReply) {
		state.isReplySuggestion = isReply;
	},
	[SET_SUGGESTION_REPLY](state, suggestion) {
		state.suggestionReply = suggestion;
	},
	[SET_SEARCH_PDF_KEYWORD](state, keyword) {
		state.searchPdfKeyword = keyword;
	},
	[SET_IS_FETCHING_PARTICIPANT](state, isFetchingParticipant) {
		state.isFetchingParticipant = isFetchingParticipant;
	},
	[SET_PARTICIPANT](state, participant) {
		state.participant = participant;
	},
	[SET_LAST_PAGE_SUGGESTIONS](state, lastPageSuggestions) {
		state.lastPageSuggestions = lastPageSuggestions;
	},
	[SET_CURRENT_PAGE_SUGGESTIONS](state, currentPageSuggestions) {
		state.currentPageSuggestions = currentPageSuggestions;
	},
	[SET_IS_SHOW_MODAL_VOTING_SHARES](state, isShow) {
		state.isShowModalVotingShares = isShow;
		if (!isShow) state.votingShares = [];
	},
	[SET_SUGGESTIONS_SORT_BY](state, sortBy) {
		state.suggestionsSortBy = sortBy;
	},
	[SET_VOTINGS](state, votings) {
		state.votings = votings;
	},
	[SET_IS_SHOW_AGENDA_MATERIAL](state, isShowAgendaMaterial) {
		state.isShowAgendaMaterial = isShowAgendaMaterial;
	},
	appendVotings(state, votings) {
		for (let i = 0; i < votings.length; i++) {
			const voting = votings[i];
			const votingExist = state.votings.find(v => v.id === voting.id);
			if (!votingExist) {
				state.votings.push(voting);
			}
		}
	},
	resetVotings(state) {
		state.votings = [];
	},
	SOCKET_EVENTS_UPDATE(state, event) {
		if (state.event && state.event.id === event.id) {
			state.event.name = event.name;
			state.event.type = event.type;
			state.event.union = event.union;
			state.event.branch = event.branch;
			state.event.start = event.start;
			state.event.end = event.end;
			state.event.settings = event.settings;
			state.event.speech_settings = event.speech_settings;
			state.event.suggestion_settings = event.suggestion_settings;
		}
	},
	SOCKET_AGENDAS_ADD(state, agenda) {
		const isExist = state.agendas.find(curr => parseInt(curr.id) === parseInt(agenda.id));
		if (!isExist) state.agendas.push(agenda);
	},
	SOCKET_AGENDAS_ADD_BULK(state, agendas) {
		for (let i = 0; i < agendas.length; i++) {
			const agenda = agendas[i];
			const isExist = state.agendas.find(curr => parseInt(curr.id) === parseInt(agenda.id));
			if (!isExist) state.agendas.push(agenda);
		}
	},
	SOCKET_AGENDAS_UPDATE(state, agenda) {
		if (agenda.status === 'active') {
			const activeAgenda = state.agendas.find(currAgenda => currAgenda.status === 'active');
			if (activeAgenda) activeAgenda.status = 'open';
		}

		// change agenda in array
		const agendaIdx = state.agendas.findIndex(curr => curr.id === agenda.id);
		if (agendaIdx !== -1) Object.assign(state.agendas[agendaIdx], agenda);

		// change selected agenda
		if (state.agenda && parseInt(agenda.id) === parseInt(state.agenda.id)) state.agenda = agenda;
	},
	SOCKET_AGENDAS_UPDATE_SETTINGS(state, agenda) {
		if (agenda.status === 'active') {
			const activeAgenda = state.agendas.find(currAgenda => currAgenda.status === 'active');
			if (activeAgenda) activeAgenda.status = 'open';
		}

		// change agenda in array
		const agendaIdx = state.agendas.findIndex(curr => curr.id === agenda.id);
		if (agendaIdx !== -1) Object.assign(state.agendas[agendaIdx], agenda);

		// change selected agenda
		if (state.agenda && parseInt(agenda.id) === parseInt(state.agenda.id)) state.agenda = agenda;
	},
	SOCKET_AGENDAS_DELETE(state, agendaIds) {
		for (let i = 0; i < agendaIds.length; i++) {
			const index = state.agendas.findIndex(curr => curr.id === agendaIds[i]);
			if (index !== -1) state.agendas.splice(index, 1);
			let indexSub = -1;
			do {
				indexSub = state.agendas.findIndex(curr => curr.parent_id === agendaIds[i]);
				if (indexSub !== -1) state.agendas.splice(indexSub, 1);
			} while (indexSub !== -1);
		}
	},
	SOCKET_AGENDAS_COPY_EVENT_SETTINGS(state, newAgendas) {
		for (let i = 0; i < newAgendas.length; i++) {
			const newAgenda = newAgendas[i];
			for (let j = 0; j < state.agendas.length; j++) {
				if (newAgenda.id === state.agendas[j].id) {
					state.agendas[j].speech_settings = newAgenda.speech_settings;
					state.agendas[j].suggestion_settings = newAgenda.suggestion_settings;
					state.agendas[j].board_advice_settings = newAgenda.board_advice_settings;
					state.agendas[j].material_settings = newAgenda.material_settings;
					break;
				}
			}
		}
	},
	SOCKET_AGENDAS_PROGRESS(state, time) {
		state.agendaTime = time;
	},
	SOCKET_AGENDAS_STOP(state) {
		state.agendaTime = 0;
	},
	SOCKET_AGENDAS_REORDER(state, agendas) {
		state.agendas = agendas;
	},
	SOCKET_SUGGESTIONS_COPY(state, suggestions) {
		// selected agenda
		if (state.agenda) {
			if (state.currentPageSuggestions === state.lastPageSuggestions && state.lastPageSuggestions === 1) {
				for (let index = 0; index < suggestions.length; index++) {
					const suggestion = suggestions[index];
					if (parseInt(state.agenda.id) === parseInt(suggestion.agenda_id)) {
						const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestion.id));
						if (suggestionIndex === -1) {
							if (state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
							else state.suggestions.push(suggestion);
						}
					}
				}
			} else if (state.currentPageSuggestions === 1 && state.suggestionsSortBy === 'desc') {
				for (let i = 0; i < suggestions.length; i++) {
					const suggestion = suggestions[i];
					state.suggestions.unshift(suggestion);
				}
			}
		}
	},
	SOCKET_SUGGESTIONS_ADD(state, suggestion) {
		if (state.currentPageSuggestions === state.lastPageSuggestions && state.lastPageSuggestions === 1) {
			// selected agenda
			if (state.agenda && parseInt(state.agenda.id) === parseInt(suggestion.agenda_id)) {
				const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestion.id));
				if (suggestionIndex === -1) {
					if (state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
					else state.suggestions.push(suggestion);
				}
			}
		} else if (state.currentPageSuggestions === 1 && state.suggestionsSortBy === 'desc') {
			state.suggestions.unshift(suggestion);
		}
	},
	SOCKET_SUGGESTIONS_ADD_BULK(state, suggestions) {
		if (state.currentPageSuggestions === state.lastPageSuggestions && state.lastPageSuggestions === 1) {
			for (let i = 0; i < suggestions.length; i++) {
				const suggestion = suggestions[i];
				// selected agenda
				if (state.agenda && parseInt(state.agenda.id) === parseInt(suggestion.agenda_id)) {
					const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestion.id));
					if (suggestionIndex === -1) {
						if (state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
						else state.suggestions.push(suggestion);
					}
				}
			}
		} else if (state.currentPageSuggestions === 1 && state.suggestionsSortBy === 'desc') {
			for (let i = 0; i < suggestions.length; i++) {
				const suggestion = suggestions[i];
				state.suggestions.unshift(suggestion);
			}
		}
	},
	SOCKET_SUGGESTIONS_UPDATE(state, suggestion) {
		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(suggestion.agenda_id)) {
			const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestion.id));
			if (suggestionIndex !== -1) {
				Object.assign(state.suggestions[suggestionIndex], suggestion);
			} else if (state.currentPageSuggestions === state.lastPageSuggestions && state.lastPageSuggestions === 1) {
				if (state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
				else state.suggestions.push(suggestion);
			} else if (state.currentPageSuggestions === 1 && state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
		}
	},
	SOCKET_SUGGESTIONS_UPDATE_BULK(state, suggestions) {
		for (let z = 0; z < suggestions.length; z++) {
			const suggestion = suggestions[z];
			// selected agenda
			if (state.agenda && parseInt(state.agenda.id) === parseInt(suggestion.agenda_id)) {
				const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestion.id));
				if (suggestionIndex !== -1) {
					Object.assign(state.suggestions[suggestionIndex], suggestion);
				} else if (state.currentPageSuggestions === state.lastPageSuggestions && state.lastPageSuggestions === 1) {
					if (state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
					else state.suggestions.push(suggestion);
				} else if (state.currentPageSuggestions === 1 && state.suggestionsSortBy === 'desc') state.suggestions.unshift(suggestion);
			}
		}
	},
	SOCKET_SUGGESTIONS_DELETE(state, suggestion) {
		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(suggestion.agenda_id)) {
			const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestion.id));
			if (suggestionIndex !== -1) state.suggestions.splice(suggestionIndex, 1);
		}
	},
	SOCKET_SUGGESTIONS_BULK_DELETE(state, suggestionIds) {
		for (let index = 0; index < suggestionIds.length; index++) {
			const suggestionId = suggestionIds[index];
			const suggestionIndex = state.suggestions.findIndex(currSuggestion => parseInt(currSuggestion.id) === parseInt(suggestionId));
			if (suggestionIndex !== -1) state.suggestions.splice(suggestionIndex, 1);
		}
	},
	SOCKET_SPEECHES_ADD(state, payload) {
		const { agendaId, speeches } = payload;
		const agendaIndex = state.agendas.findIndex(curr => curr.id === agendaId);
		if (agendaIndex !== -1) state.agendas[agendaIndex].speeches = speeches;

		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(agendaId)) state.agenda.speeches = speeches;
	},
	SOCKET_SPEECHES_DELETE(state, payload) {
		const { agendaId, speeches } = payload;
		const agendaIndex = state.agendas.findIndex(curr => curr.id === agendaId);
		if (agendaIndex !== -1) state.agendas[agendaIndex].speeches = speeches;

		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(agendaId)) state.agenda.speeches = speeches;
	},
	SOCKET_SPEECHES_PAUSE(state, speech) {
		const agendaIndex = state.agendas.findIndex(curr => curr.id === speech.agenda_id);
		if (agendaIndex !== -1) {
			const speechIndex = state.agendas[agendaIndex].speeches.findIndex(curr => curr.id === speech.id);
			if (speechIndex !== -1) state.agendas[agendaIndex].speeches[speechIndex].is_paused = speech.is_paused;
		}
	},
	SOCKET_SPEECHES_RESUME(state, speech) {
		const agendaIndex = state.agendas.findIndex(curr => curr.id === speech.agenda_id);
		if (agendaIndex !== -1) {
			const speechIndex = state.agendas[agendaIndex].speeches.findIndex(curr => curr.id === speech.id);
			if (speechIndex !== -1) state.agendas[agendaIndex].speeches[speechIndex].is_paused = speech.is_paused;
		}
	},
	SOCKET_SPEECHES_PROGRESS(state, value) {
		state.speechProgress = value;
	},
	SOCKET_SPEECHES_STOP(state) {
		state.speechProgress = 0;
	},
	SOCKET_SPEECHES_UPDATE(state, speech) {
		const agendaIndex = state.agendas.findIndex(curr => curr.id === speech.agenda_id);
		let speechIndex = -1;
		if (agendaIndex !== -1) speechIndex = state.agendas[agendaIndex].speeches.findIndex(curr => curr.id === speech.id);
		if (agendaIndex !== -1 && speechIndex !== -1) Object.assign(state.agendas[agendaIndex].speeches[speechIndex], speech);
		if (state.agenda && parseInt(state.agenda.id) === parseInt(speech.agenda_id) && state.agenda.speeches) {
			speechIndex = state.agenda.speeches.findIndex(curr => parseInt(curr.id) === parseInt(speech.id));
			if (speechIndex !== -1) Object.assign(state.agenda.speeches[speechIndex], speech);
		}
	},
	SOCKET_SPEECHES_UNDO(state, speech) {
		if (speech) {
			const agendaIndex = state.agendas.findIndex(curr => curr.id === speech.agenda_id);
			let speechIndex = -1;
			if (agendaIndex !== -1) speechIndex = state.agendas[agendaIndex].speeches.findIndex(curr => curr.id === speech.id);
			if (agendaIndex !== -1 && speechIndex !== -1) Object.assign(state.agendas[agendaIndex].speeches[speechIndex], speech);
			if (state.agenda && parseInt(state.agenda.id) === parseInt(speech.agenda_id) && state.agenda.speeches) {
				speechIndex = state.agenda.speeches.findIndex(curr => parseInt(curr.id) === parseInt(speech.id));
				if (speechIndex !== -1) Object.assign(state.agenda.speeches[speechIndex], speech);
			}
		}

		// Set speech progress to 0
		state.speechProgress = 0;
	},
	SOCKET_SPEECHES_UPDATE_BULK(state, speeches) {
		for (let i = 0; i < speeches.length; i++) {
			const speech = speeches[i];
			const agendaIndex = state.agendas.findIndex(curr => curr.id === speech.agenda_id);
			let speechIndex = -1;
			if (agendaIndex !== -1) speechIndex = state.agendas[agendaIndex].speeches.findIndex(curr => curr.id === speech.id);
			if (agendaIndex !== -1 && speechIndex !== -1) Object.assign(state.agendas[agendaIndex].speeches[speechIndex], speech);
			if (state.agenda && parseInt(state.agenda.id) === parseInt(speech.agenda_id) && state.agenda.speeches) {
				speechIndex = state.agenda.speeches.findIndex(curr => parseInt(curr.id) === parseInt(speech.id));
				if (speechIndex !== -1) Object.assign(state.agenda.speeches[speechIndex], speech);
			}
		}
	},
	SOCKET_SPEECHES_ORDER(state, payload) {
		const { agendaId, speeches } = payload;
		const agendaIndex = state.agendas.findIndex(curr => curr.id === agendaId);
		if (agendaIndex !== -1) state.agendas[agendaIndex].speeches = speeches;
		if (state.agenda && parseInt(state.agenda.id) === parseInt(agendaId)) state.agenda.speeches = speeches;
	},
	SOCKET_SPEECHES_LAST(state, payload) {
		const agendaId = payload.agenda_id;
		const order = payload.order;
		const agendaIndex = state.agendas.findIndex(agenda => agenda.id === agendaId);
		if (agendaIndex !== -1) {
			const agenda = state.agendas[agendaIndex];

			// update agenda speech and suggestion settings
			const speechSettings = JSON.parse(state.agendas[agendaIndex].speech_settings);
			speechSettings.is_enabled = false;
			speechSettings.normal_enabled = false;
			speechSettings.reply_enabled = false;
			speechSettings.postpone_enabled = false;

			const suggestionSettings = JSON.parse(state.agendas[agendaIndex].suggestion_settings);
			suggestionSettings.is_enabled = false;
			suggestionSettings.postpone_enabled = false;

			if (state.agenda) {
				state.agenda.speech_settings = JSON.stringify(speechSettings);
				state.agenda.suggestion_settings = JSON.stringify(suggestionSettings);
			}

			const speechesLength = agenda.speeches.length;
			for (let speechIndex = speechesLength - 1; speechIndex >= 0; speechIndex--) {
				const speech = agenda.speeches[speechIndex];
				if (speech && speech.order > order) agenda.speeches.splice(speechIndex, 1);
				// selected agenda
				if (state.agenda && parseInt(state.agenda.id) === parseInt(agendaId)) {
					if (speech && speech.order > order) state.agenda.speeches.splice(speechIndex, 1);
				}
			}
		}
	},
	SOCKET_PARTICIPANTS_ADD(state, participant) {
		if (state.participants) state.participants.push(participant);
	},
	SOCKET_PARTICIPANTS_ADD_BULK(state, participants) {
		if (state.participants) {
			for (let i = 0; i < participants.length; i++) {
				const participant = participants[i];
				if (!participant.existing) state.participants.push(participant);
			}
		}
	},
	SOCKET_PARTICIPANTS_UPDATE(state, participant) {
		if (state.participants) {
			const index = state.participants.findIndex(curr => curr.id === participant.id);
			if (index !== -1) Object.assign(state.participants[index], participant);
		}

		// check current user logged
		if (state.participant && parseInt(state.participant.id) === parseInt(participant.id)) {
			// me
			state.participant.is_active = participant.is_active;
			state.participant.is_checked_in = participant.is_checked_in;
			state.participant.is_checked_out = participant.is_checked_out;
			state.participant.roles = participant.roles;
			state.participant.capabilities = participant.capabilities;
			state.participant.leave_time = participant.leave_time;
			state.participant.checked_floor_at = participant.checked_floor_at;
		}
	},
	SOCKET_PARTICIPANTS_CHECK_LEAVE(state, participant) {
		// check current user logged
		if (state.participant && parseInt(state.participant.id) === parseInt(participant.id)) window.location.reload();
	},
	SOCKET_PARTICIPANTS_DELETE(state, participantsIds) {
		if (state.participants) {
			for (let i = 0; i < participantsIds.length; i++) {
				const index = state.participants.findIndex(curr => curr.id === participantsIds[i]);
				if (index !== -1) state.participants.splice(index, 1);
			}
		}
	},
	SOCKET_PARTICIPANTS_FORCE_LOGOUT(state, participantIds) {
		if (state.participant) {
			const index = participantIds.findIndex(participantId => parseInt(participantId) === parseInt(state.participant.id));
			if (index !== -1) {
				localStorage.removeItem('access_token');
				localStorage.removeItem('id_token');
				localStorage.removeItem('expires_at');
				localStorage.removeItem('access_agenda_code');
				localStorage.removeItem('access_agenda_id');
				window.location.href = '/login';
			}
		}
	},
	SOCKET_TEAMS_ADD(state, team) {
		state.teams.push(team);
	},
	SOCKET_TEAMS_UPDATE(state, team) {
		if (state.teams.length > 0) {
			state.teams = state.teams.map((current) => {
				if (current.id === team.id) return Object.assign({}, current, team);
				return current;
			});
		}
	},
	SOCKET_TEAMS_DELETE(state, teamIds) {
		for (let i = 0; i < teamIds.length; i++) {
			const teamId = teamIds[i];
			for (let j = 0; j < state.teams.length; j++) {
				const current = state.teams[j];
				if (current.id === teamId) {
					state.teams.splice(j, 1);
					break;
				}
			}
		}
	},
	SOCKET_AGENDA_MATERIALS_ADD(state, agendaMaterial) {
		const agendaIndex = state.agendas.findIndex(curr => curr.id === agendaMaterial.agenda_id);
		if (agendaIndex !== -1) state.agendas[agendaIndex].materials.unshift(agendaMaterial);

		// selected agenda
		if (state.agenda && state.agenda.id === agendaMaterial.agenda_id) {
			const agenda = duplicateVar(state.agenda);
			agenda.materials.unshift(agendaMaterial);
			Object.assign(state.agenda, agenda);
		}
	},
	SOCKET_AGENDA_MATERIALS_UPDATE(state, agendaMaterial) {
		const agendaIndex = state.agendas.findIndex(curr => parseInt(curr.id) === parseInt(agendaMaterial.agenda_id));
		if (agendaIndex !== -1) {
			const materialIdx = state.agendas[agendaIndex].materials.findIndex(curr => parseInt(curr.id) === parseInt(agendaMaterial.id));
			if (materialIdx !== -1) Object.assign(state.agendas[agendaIndex].materials[materialIdx], agendaMaterial);
		}

		// selected agenda
		if (state.agenda && state.agenda.id === agendaMaterial.agenda_id) {
			const materialIdx = state.agenda.materials.findIndex(curr => parseInt(curr.id) === parseInt(agendaMaterial.id));
			if (materialIdx !== -1) Object.assign(state.agenda.materials[materialIdx], agendaMaterial);
		}
	},
	SOCKET_AGENDA_MATERIALS_DELETE(state, agendaMaterial) {
		const agendaIndex = state.agendas.findIndex(curr => parseInt(curr.id) === parseInt(agendaMaterial.agenda_id));
		if (agendaIndex !== -1) {
			const materialIdx = state.agendas[agendaIndex].materials.findIndex(curr => parseInt(curr.id) === parseInt(agendaMaterial.id));
			if (materialIdx !== -1) state.agendas[agendaIndex].materials.splice(materialIdx, 1);
		}

		// selected agenda
		if (state.agenda && state.agenda.id === agendaMaterial.agenda_id) {
			const materialIdx = state.agenda.materials.findIndex(curr => parseInt(curr.id) === parseInt(agendaMaterial.id));
			if (materialIdx !== -1) state.agenda.materials.splice(materialIdx, 1);
		}
	},
	SOCKET_AGENDA_MATERIALS_BROADCAST(state, agendaMaterial) {
		state.selectedAgendaMaterial = agendaMaterial;
		state.isShowAgendaMaterial = true;
	},
	SOCKET_AGENDA_LOG_ADD(state, agendaLog) {
		const agendaIndex = state.agendas.findIndex(curr => curr.id === agendaLog.agenda_id);
		if (agendaIndex !== -1) state.agendas[agendaIndex].agenda_logs.push(agendaLog);

		// selected agenda
		if (state.agenda && state.agenda.id === agendaLog.agenda_id) {
			const agenda = duplicateVar(state.agenda);
			agenda.agenda_logs.push(agendaLog);
			Object.assign(state.agenda, agenda);
		}
	},
	SOCKET_AGENDA_LOG_UPDATE(state, agendaLog) {
		const agendaIndex = state.agendas.findIndex(curr => parseInt(curr.id) === parseInt(agendaLog.agenda_id));
		if (agendaIndex !== -1) {
			const logIdx = state.agendas[agendaIndex].agenda_logs.findIndex(curr => parseInt(curr.id) === parseInt(agendaLog.id));
			if (logIdx !== -1) Object.assign(state.agendas[agendaIndex].agenda_logs[logIdx], agendaLog);
		}

		// selected agenda
		if (state.agenda && state.agenda.id === agendaLog.agenda_id) {
			const logIdx = state.agenda.agenda_logs.findIndex(curr => parseInt(curr.id) === parseInt(agendaLog.id));
			if (logIdx !== -1) Object.assign(state.agenda.agenda_logs[logIdx], agendaLog);
		}
	},
	SOCKET_AGENDA_LOG_DELETE(state, agendaLog) {
		const agendaIndex = state.agendas.findIndex(curr => parseInt(curr.id) === parseInt(agendaLog.agenda_id));
		if (agendaIndex !== -1) {
			const logIdx = state.agendas[agendaIndex].agenda_logs.findIndex(curr => parseInt(curr.id) === parseInt(agendaLog.id));
			if (logIdx !== -1) state.agendas[agendaIndex].agenda_logs.splice(logIdx, 1);
		}

		// selected agenda
		if (state.agenda && state.agenda.id === agendaLog.agenda_id) {
			const logIdx = state.agenda.agenda_logs.findIndex(curr => parseInt(curr.id) === parseInt(agendaLog.id));
			if (logIdx !== -1) state.agenda.agenda_logs.splice(logIdx, 1);
		}
	},
	SOCKET_INTRO_SPEAKERS_ADD(state, intro) {
		if (state.agendas) {
			const agendaIdx = state.agendas.findIndex(currAgenda => parseInt(currAgenda.id) === parseInt(intro.agenda_id));
			if (agendaIdx !== -1) {
				const agenda = duplicateVar(state.agendas[agendaIdx]);
				agenda.intro_speakers.push(intro);
				Object.assign(state.agendas[agendaIdx], agenda);
			}
		}
	},
	SOCKET_INTRO_SPEAKERS_UPDATE(state, intro) {
		const agendaIndex = state.agendas.findIndex(curr => curr.id === intro.agenda_id);
		if (agendaIndex !== -1) {
			const introIdx = state.agendas[agendaIndex].intro_speakers.findIndex(curr => curr.id === intro.id);
			if (introIdx !== -1) Object.assign(state.agendas[agendaIndex].intro_speakers[introIdx], intro);
		}

		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(intro.agenda_id)) {
			const introIdx = state.agenda.intro_speakers.findIndex(curr => curr.id === intro.id);
			if (introIdx !== -1) Object.assign(state.agenda.intro_speakers[introIdx], intro);
		}
	},
	SOCKET_INTRO_SPEAKERS_DELETE(state, introIds) {
		for (let i = 0; i < introIds.length; i++) {
			const introId = introIds[i];
			for (let j = 0; j < state.agendas.length; j++) {
				const introIdx = state.agendas[j].intro_speakers.findIndex(curr => parseInt(curr.id) === parseInt(introId));
				if (introIdx !== -1) {
					state.agendas[j].intro_speakers.splice(introIdx, 1);
					break;
				}
			}

			// selected agenda
			if (state.agenda) {
				const introIdx = state.agenda.intro_speakers.findIndex(curr => parseInt(curr.id) === parseInt(introId));
				if (introIdx !== -1) {
					state.agenda.intro_speakers.splice(introIdx, 1);
				}
			}
		}
	},
	SOCKET_SUGGESTIONS_COMPARES(state, suggestions) {
		state.isShareSuggestions = false;
		state.isCompareSuggestions = true;
		state.isVotingSuggestions = false;
		state.suggestionsShared = suggestions;
		state.isShowModalSuggestions = true;
	},
	SOCKET_SUGGESTIONS_SHARES(state, suggestions) {
		state.isShareSuggestions = true;
		state.isCompareSuggestions = false;
		state.isVotingSuggestions = false;
		state.suggestionsShared = suggestions;
		state.isShowModalSuggestions = true;
	},
	SOCKET_SUGGESTIONS_VOTINGS(state, payload) {
		const voting = payload.voting;
		const suggestions = payload.suggestions;

		state.voting = voting;
		state.isShareSuggestions = false;
		state.isCompareSuggestions = false;
		state.isVotingSuggestions = true;
		if (voting.type === 'suggestion') {
			state.suggestionsShared = suggestions;
		} else {
			state.suggestionsShared = [];
		}

		// Check voter
		if (state) {
			const participant = state.participant;
			const event = state.event;
			const isLeave = isParticipantNewLeave(participant);
			const isHaveVoteRight = isParticipantHaveVoteRight(participant);
			const isActive = isParticipantActive(participant);
			const isParticipantAtTheFloor = isEventCheckFloorEnabledAndParticipantAtTheFloor(event, participant);
			const isAllowed = !isLeave && isActive && isHaveVoteRight && isParticipantAtTheFloor;
			let isVoter = isAllowed;
			if (event) {
				const isCheckInRequired = isEventCheckInRequired(event);
				if (isCheckInRequired) {
					const isCheckIn = isParticipantCheckIn(participant);
					isVoter = isAllowed && isCheckIn;
				}
			}

			// If not voter
			if (!isVoter) return;

			// Show modal
			state.isShowModalSuggestions = true;
		}
	},
	SOCKET_VOTINGS_PROGRESS(state, value) {
		state.votingTime = value;
	},
	SOCKET_VOTINGS_START(state, voting) {
		// Check voter
		if (state) {
			const participant = state.participant;
			const event = state.event;
			const isLeave = isParticipantNewLeave(participant);
			const isHaveVoteRight = isParticipantHaveVoteRight(participant);
			const isActive = isParticipantActive(participant);
			const isParticipantAtTheFloor = isEventCheckFloorEnabledAndParticipantAtTheFloor(event, participant);
			const isAllowed = !isLeave && isActive && isHaveVoteRight && isParticipantAtTheFloor;
			let isVoter = isAllowed;
			if (event) {
				const isCheckInRequired = isEventCheckInRequired(event);
				if (isCheckInRequired) {
					const isCheckIn = isParticipantCheckIn(participant);
					isVoter = isAllowed && isCheckIn;
				}
			}

			// If not voter
			if (!isVoter) return;
		}

		// clear previous result (if available)
		const votingIdx = state.votings.findIndex(curr => curr.id === voting.id);
		if (votingIdx !== -1) {
			const votingOptions = state.votings[votingIdx].voting_options;
			for (let j = 0; j < votingOptions.length; j++) {
				votingOptions[j].voting_results = [];
			}
		}

		if (state.selectedVoting && state.selectedVoting.id === voting.id) {
			const selectedVoting = duplicateVar(state.selectedVoting);
			for (let i = 0; i < selectedVoting.voting_options.length; i++) {
				selectedVoting.voting_options[i].voting_results = [];
			}

			state.selectedVoting = selectedVoting;
		}

		state.voting = voting;
		state.isShowModalVoting = true;
	},
	SOCKET_VOTINGS_STOP(state) {
		state.isShowModalSuggestions = false;
		state.isShowModalVoting = false;
		state.voting = null;
	},
	SOCKET_VOTINGS_ADD(state, voting) {
		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(voting.agenda_id)) {
			state.votings.unshift(voting);
		}
	},
	SOCKET_VOTINGS_UPDATE(state, voting) {
		// selected agenda
		if (state.agenda && parseInt(state.agenda.id) === parseInt(voting.agenda_id)) {
			const votingIdx = state.votings.findIndex(curr => curr.id === voting.id);
			if (votingIdx !== -1) Object.assign(state.votings[votingIdx], voting);
		}

		// selected voting
		if (state.selectedVoting && parseInt(state.selectedVoting.id) === parseInt(voting.id)) {
			// Object.assign(state.selectedVoting, voting);
			state.selectedVoting = voting;
		}
	},
	SOCKET_VOTINGS_DELETE(state, votingIds) {
		for (let i = 0; i < votingIds.length; i++) {
			const votingId = votingIds[i];

			// selected agenda
			if (state.agenda) {
				const votingIndex = state.votings.findIndex(curr => parseInt(curr.id) === parseInt(votingId));
				if (votingIndex !== -1) state.votings.splice(votingIndex, 1);
			}

			// Selected Voting
			if (state.selectedVoting && parseInt(state.selectedVoting.id) === parseInt(votingId)) {
				state.selectedVoting = null;
			}
		}
	},
	SOCKET_VOTING_OPTIONS_ADD(state, option) {
		const votingIdx = state.votings.findIndex(curr => parseInt(curr.id) === parseInt(option.voting_id));
		if (votingIdx !== -1) {
			const voting = duplicateVar(state.votings[votingIdx]);
			voting.voting_options.push(option);
			Object.assign(state.votings[votingIdx], voting);
		}

		// Selected Voting
		if (state.selectedVoting && parseInt(state.selectedVoting.id) === parseInt(option.voting_id)) {
			const selectedVoting = duplicateVar(state.selectedVoting);
			selectedVoting.voting_options.push(option);

			// Refresh voting data
			state.selectedVoting = selectedVoting;
		}
	},
	SOCKET_VOTING_OPTIONS_UPDATE(state, option) {
		const votingIdx = state.votings.findIndex(curr => curr.id === option.voting_id);
		if (votingIdx !== -1) {
			const optionIdx = state.votings[votingIdx].voting_options.findIndex(curr => curr.id === option.id);
			if (optionIdx !== -1) {
				Object.assign(state.votings[votingIdx].voting_options[optionIdx], option);
			}
		}

		// Selected Voting
		if (state.selectedVoting && parseInt(state.selectedVoting.id) === parseInt(option.voting_id)) {
			const selectedVoting = duplicateVar(state.selectedVoting);
			const optionIdx = selectedVoting.voting_options.findIndex(currOption => parseInt(currOption.id) === parseInt(option.id));
			if (optionIdx !== -1) {
				Object.assign(selectedVoting.voting_options[optionIdx], option);
			}

			// Refresh voting data
			state.selectedVoting = selectedVoting;
		}
	},
	SOCKET_VOTING_OPTIONS_DELETE(state, optionIds) {
		for (let i = 0; i < optionIds.length; i++) {
			const optionId = optionIds[i];
			for (let k = 0; k < state.votings.length; k++) {
				const voting = state.votings[k];
				const optionIdx = voting.voting_options.findIndex(currOption => parseInt(currOption.id) === parseInt(optionId));
				if (optionIdx !== -1) {
					voting.voting_options.splice(optionIdx, 1);
					break;
				}
			}

			// Selected Voting
			if (state.selectedVoting) {
				const selectedVoting = duplicateVar(state.selectedVoting);
				const optionIdx = selectedVoting.voting_options.findIndex(currOption => parseInt(currOption.id) === parseInt(optionId));
				if (optionIdx !== -1) selectedVoting.voting_options.splice(optionIdx, 1);

				// Refresh voting data
				state.selectedVoting = selectedVoting;
			}
		}
	},
	SOCKET_VOTING_RESULTS_ADD(state, payload) {
		const { votingId, votingResults } = payload;
		let { votingResult } = payload;

		if (votingResult === null) votingResult = votingResults[0];

		// selected agenda
		if (state.agenda) {
			for (let i = 0; i < state.votings.length; i++) {
				const voting = duplicateVar(state.votings[i]);
				if (parseInt(voting.id) === parseInt(votingId)) {
					const votingOptionIdx = voting.voting_options.findIndex(curr => curr.id === votingResult.voting_option_id);
					if (votingOptionIdx !== -1) {
						if (votingResults.length === 0) {
							const option = duplicateVar(voting.voting_options[votingOptionIdx]);
							const resultIdx = option.voting_results.findIndex(curr => curr.id === votingResult.id);
							if (resultIdx === -1) option.voting_results.push(votingResult);
							Object.assign(voting.voting_options[votingOptionIdx], option);
						} else {
							for (let idx = 0; idx < votingResults.length; idx++) {
								const optionIdx = voting.voting_options.findIndex(curr => curr.id === votingResults[idx].voting_option_id);
								if (optionIdx !== -1) {
									const resultIdx = voting.voting_options[optionIdx].voting_results.findIndex(curr => curr.id === votingResults[idx].id);
									if (resultIdx === -1) voting.voting_options[optionIdx].voting_results.push(votingResults[idx]);
								}
							}
						}
					}

					Object.assign(state.votings[i], voting);
					break;
				}
			}
		}

		// selected voting
		if (state.selectedVoting && parseInt(state.selectedVoting.id) === parseInt(votingId)) {
			const voting = duplicateVar(state.selectedVoting);
			const votingOptionIdx = voting.voting_options.findIndex(curr => curr.id === votingResult.voting_option_id);
			if (votingOptionIdx !== -1) {
				if (votingResults.length === 0) {
					const option = duplicateVar(voting.voting_options[votingOptionIdx]);
					const resultIdx = option.voting_results.findIndex(curr => curr.id === votingResult.id);
					if (resultIdx === -1) option.voting_results.push(votingResult);
					Object.assign(voting.voting_options[votingOptionIdx], option);
				} else {
					for (let idx = 0; idx < votingResults.length; idx++) {
						const optionIdx = voting.voting_options.findIndex(curr => curr.id === votingResults[idx].voting_option_id);
						if (optionIdx !== -1) {
							const resultIdx = voting.voting_options[optionIdx].voting_results.findIndex(curr => curr.id === votingResults[idx].id);
							if (resultIdx === -1) voting.voting_options[optionIdx].voting_results.push(votingResults[idx]);
						}
					}
				}
			}

			state.selectedVoting = voting;
			// Object.assign(state.selectedVoting, voting);
		}

		// Voting Shares
		if (state.votingShares && state.votingShares.length > 0) {
			for (let i = 0; i < state.votingShares.length; i++) {
				const voting = duplicateVar(state.votingShares[i]);
				if (parseInt(voting.id) === parseInt(votingId)) {
					const votingOptionIdx = voting.voting_options.findIndex(curr => curr.id === votingResult.voting_option_id);
					if (votingOptionIdx !== -1) {
						if (votingResults.length === 0) {
							const option = duplicateVar(voting.voting_options[votingOptionIdx]);
							const resultIdx = option.voting_results.findIndex(curr => curr.id === votingResult.id);
							if (resultIdx === -1) option.voting_results.push(votingResult);
							Object.assign(voting.voting_options[votingOptionIdx], option);
						} else {
							for (let idx = 0; idx < votingResults.length; idx++) {
								const optionIdx = voting.voting_options.findIndex(curr => curr.id === votingResults[idx].voting_option_id);
								if (optionIdx !== -1) {
									const resultIdx = voting.voting_options[optionIdx].voting_results.findIndex(curr => curr.id === votingResults[idx].id);
									if (resultIdx === -1) voting.voting_options[optionIdx].voting_results.push(votingResults[idx]);
								}
							}
						}
					}

					Object.assign(state.votingShares[i], voting);
					break;
				}
			}
		}
	},
	SOCKET_VOTINGS_SCREEN(state, votings) {
		state.isShowModalVotingShares = true;
		state.votingShares = votings;
	},
};

const actions = {
	resetStates({ commit }) {
		commit(RESET_STATES);
	},
	resetAgendas({ commit }) {
		commit(RESET_AGENDAS);
	},
	getEvent({ commit, getters, dispatch }, eventSlug) {
		// gettersTmp = getters;
		if (getters.isFetching) return;

		const params = {};
		commit(SET_IS_FETCHING, true);
		eventsApi.getDetailsBySlug(eventSlug, params, (response) => {
			// Set event
			const event = response.data;
			commit(SET_EVENT, event);
			commit(SET_IS_FETCHING, false);
			document.title = `Now - ${event.name} - MyMeet`;

			// Fetch others
			commit(SET_PARTICIPANT_PAGE, 1);
			dispatch('getParticipantMe', event.id);
			dispatch('getParticipants', event.id);
			dispatch('getActiveVoting', event.slug);
			dispatch('getTeams', event.id);
		}, () => {
			commit(SET_IS_FETCHING, false);
		});
	},
	getParticipantMe({ commit }, eventId) {
		const params = {
			event_id: eventId,
		};
		commit(SET_IS_FETCHING_PARTICIPANT, true);
		participantApi.getMe(params, (response) => {
			const participant = response;
			commit(SET_PARTICIPANT, participant);
			commit(SET_STARTING_ROLE, participant);
			commit(SET_IS_FETCHING_PARTICIPANT, false);
		}, () => {
			commit(SET_IS_FETCHING_PARTICIPANT, false);
		});
	},
	getParticipants({ commit, getters, dispatch }, eventId) {
		const isPaginated = getters.participantPaginated;
		const page = getters.participantPage;
		const params = {
			event_id: eventId,
		};

		if (isPaginated) {
			params.is_paginated = getters.participantPaginated;
			params.limit = getters.participantLimit;
			params.page = getters.participantPage;
			params.order_by = 'id';
			params.sort_by = 'asc';
		}

		commit(SET_IS_FETCHING_PARTICIPANTS, true);
		const callback = (response) => {
			if (isPaginated) {
				const lastPage = response.data.last_page;
				if (page < lastPage) {
					const newPage = page + 1;
					commit(SET_PARTICIPANT_PAGE, newPage);
					dispatch('getParticipants', eventId);
				}

				const participants = response.data.data;
				commit(ADD_PARTICIPANTS, participants);
			} else {
				const participants = response.data;
				commit(SET_PARTICIPANTS, participants);
			}

			commit(SET_IS_FETCHING_PARTICIPANTS, false);
		};
		const errorCallback = () => {
			commit(SET_IS_FETCHING_PARTICIPANTS, false);
		};
		participantApi.getList(params, callback, errorCallback);
	},
	getActiveVoting({ commit, getters }, eventSlug) {
		votingsApi.getActive(eventSlug, (response) => {
			const voting = response.data;
			if (voting) {
				commit(SET_VOTING, {
					data: voting,
					getters,
				});
			}
		}, () => {
		});
	},
	getTeams({ commit }, eventId) {
		const params = {
			event_id: eventId,
		};
		commit(SET_IS_FETCHING_TEAMS, true);
		teamApi.getList(params, (response) => {
			const teams = response.data;
			commit(SET_TEAMS, teams);
			commit(SET_IS_FETCHING_TEAMS, false);
		}, () => {
			commit(SET_IS_FETCHING_TEAMS, false);
		});
	},
	getAgendas({ commit, dispatch }, eventSlug) {
		const params = {
			event_slug: eventSlug,
			is_material: 1,
			is_speeches: 1,
			is_introSpeaker: 1,
			is_agendaBookmark: 1,
			is_agenda_logs: 1,
		};

		commit(SET_IS_FETCHING_AGENDAS, true);
		agendaApi.listByEventSlug(params, (response) => {
			const agendas = response.data;
			commit(SET_AGENDAS, agendas);
			commit(SET_IS_FETCHING_AGENDAS, false);

			dispatch('setStateByParam', agendas);
		}, () => {
			commit(SET_IS_FETCHING_AGENDAS, false);
		});
	},
	setStateByParam({ commit, getters }, agendas) {
		const urlString = window.location.href;
		const url = new URL(urlString);

		// Set agenda
		const agendaId = url.searchParams.get('agenda_id');
		if (agendaId) {
			const agenda = agendas.find(curr => parseInt(curr.id) === parseInt(agendaId));
			commit(SET_AGENDA, { agenda, getters });
		}

		// Set asset
		const asset = url.searchParams.get('asset');
		if (asset) commit(SET_SELECTED_AGENDA_ASSET, asset);
	},
	setAgenda({ commit, getters }, agenda) {
		commit(SET_AGENDA, { agenda, getters });
	},
	setAgendaBookmark({ commit }, { agendaId, agendaBookmark }) {
		commit(SET_AGENDA_BOOKMARK, { agendaId, agendaBookmark });
	},
	setShowAddSuggestion({ commit }, isSet) {
		commit(SET_SHOW_ADD_SUGGESTION, isSet);
	},
	setShowAddRecommendation({ commit }, isSet) {
		commit(SET_SHOW_ADD_RECOMMENDATION, isSet);
	},
	setShowAddSpeech({ commit }, args) {
		commit(SET_SHOW_ADD_SPEECH, args);
	},
	setCurrentRole({ commit }, role) {
		commit(SET_CURRENT_ROLE, role);
	},
	changeIsShowModalSuggestions({ commit }, isShow) {
		commit(SET_IS_SHOW_MODAL_SUGGESTIONS, isShow);
	},
	setShowModalMaterialChooser({ commit }, isSet) {
		commit(SET_SHOW_MODAL_MATERIAL_CHOOSER, isSet);
	},
	setSelectedMaterial({ commit }, payload) {
		commit(SET_SELECTED_MATERIAL, payload);
	},
	setSelectedVoting({ commit }, voting) {
		commit(SET_SELECTED_VOTING, voting);
	},
	setShowModalVoting({ commit }, isSet) {
		commit(SET_SHOW_MODAL_VOTING, isSet);
	},
	setShowFormAddSuggestion({ commit }, isSet) {
		commit(SET_SHOW_FORM_ADD_SUGGESTION, isSet);
	},
	setShowViewMaterials({ commit }, isSet) {
		commit(SET_SHOW_VIEW_MATERIALS, isSet);
	},
	setSelectedAgendaAsset({ commit }, value) {
		commit(SET_SELECTED_AGENDA_ASSET, value);
	},
	setSelectedSuggestions({ commit }, suggestions) {
		commit(SET_SELECTED_SUGGESTIONS, suggestions);
	},
	setSelectedSuggestion({ commit }, suggestion) {
		commit(SET_SELECTED_SUGGESTION, suggestion);
	},
	setFilteredSuggestions({ commit }, suggestions) {
		commit(SET_FILTERED_SUGGESTIONS, suggestions);
	},
	setSuggestionCategory({ commit, getters }, category) {
		const currentCategory = getters.suggestionCategory;
		const newCategory = currentCategory !== category ? category : 'suggestion';
		commit(SET_SUGGESTION_CATEGORY, newCategory);
	},
	setIsReplySuggestion({ commit }, isReply) {
		commit(SET_IS_REPLY_SUGGESTION, isReply);
	},
	setSuggestionReply({ commit }, suggestion) {
		commit(SET_SUGGESTION_REPLY, suggestion);
	},
	setSearchPdfKeyword({ commit }, keyword) {
		commit(SET_SEARCH_PDF_KEYWORD, keyword);
	},
	setSuggestions({ commit }, suggestions) {
		commit(SET_SUGGESTIONS, suggestions);
	},
	setLastPageSuggestions({ commit }, lastPage) {
		commit(SET_LAST_PAGE_SUGGESTIONS, lastPage);
	},
	setCurrentPageSuggestions({ commit }, page) {
		commit(SET_CURRENT_PAGE_SUGGESTIONS, page);
	},
	setShowModalVotingShares({ commit }, isShow) {
		commit(SET_IS_SHOW_MODAL_VOTING_SHARES, isShow);
	},
	setSuggestionsSortBy({ commit }, sortBy) {
		commit(SET_SUGGESTIONS_SORT_BY, sortBy);
	},
	setVotings({ commit }, votings) {
		commit(SET_VOTINGS, votings);
	},
	appendVotings({ commit }, votings) {
		commit('appendVotings', votings);
	},
	resetVotings({ commit }) {
		commit('resetVotings');
	},
	setShowAgendaMaterial({ commit }, isShow) {
		commit(SET_IS_SHOW_AGENDA_MATERIAL, isShow);
	},
	resetParticipants({ commit }) {
		commit(SET_PARTICIPANT_PAGE, 1);
		commit(SET_PARTICIPANTS, []);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
